import React, { createContext, useCallback, useContext, useState } from 'react';
import { IntakePatient } from '~/features/capacity-management/types/intake.types';

interface QueueEntry {
  referralId: string;
  patient: IntakePatient;
}

interface IntakeQueueContextType {
  queuedPatients: IntakePatient[];
  getEntryByReferralId: (referralId: string) => QueueEntry | undefined;
  addToQueue: (referralId: string, patient: IntakePatient) => void;
  markAssigned: (patientId: number, providerName: string, intakeDate: Date) => void;
  pendingCount: number;
  clearAll: () => void;
}

const IntakeQueueContext = createContext<IntakeQueueContextType>({
  queuedPatients: [],
  getEntryByReferralId: () => undefined,
  addToQueue: () => {},
  markAssigned: () => {},
  pendingCount: 0,
  clearAll: () => {},
});

export const IntakeQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<QueueEntry[]>([]);

  const addToQueue = useCallback((referralId: string, patient: IntakePatient) => {
    setEntries((prev) => {
      if (prev.some((e) => e.referralId === referralId)) return prev;
      return [{ referralId, patient }, ...prev];
    });
  }, []);

  const markAssigned = useCallback((patientId: number, providerName: string, intakeDate: Date) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.patient.id === patientId
          ? {
              ...e,
              patient: {
                ...e.patient,
                status: 'assigned',
                assignedProviderName: providerName,
                intakeVisitDate: intakeDate,
              },
            }
          : e
      )
    );
  }, []);

  const getEntryByReferralId = useCallback(
    (referralId: string) => entries.find((e) => e.referralId === referralId),
    [entries]
  );

  const clearAll = useCallback(() => {
    setEntries([]);
  }, []);

  const queuedPatients = entries.map((e) => e.patient);
  const pendingCount = entries.filter((e) => e.patient.status === 'unassigned').length;

  return (
    <IntakeQueueContext.Provider
      value={{ queuedPatients, getEntryByReferralId, addToQueue, markAssigned, pendingCount, clearAll }}
    >
      {children}
    </IntakeQueueContext.Provider>
  );
};

export const useIntakeQueue = () => useContext(IntakeQueueContext);
